// What a unit costs at a quantity (docs/suppliers.md, "Delivered or picked up"). The first two steps are what the
// server books — the tier the booking's quantity reaches, less the supplier's discount; freight and Skonto are never
// booked and only make the Einkauf tab's "bei 10 t frei Baustelle" comparable across suppliers.

/**
 * The price for `quantity` in `currency`: the tier with the greatest `minQuantity` up to the quantity that has a price
 * in that currency, else the base price. Without a quantity the base price — as the server picks it.
 */
export function tierPrice(prices, tiers, quantity, currency) {
  const reached = reachedTier(tiers, quantity, currency)
  return reached ? reached.prices[currency] : (prices?.[currency] ?? null)
}

/** The tier a quantity reaches in a currency, or null for the base price. */
export function reachedTier(tiers, quantity, currency) {
  if (!(Number(quantity) > 0)) return null
  return (
    (tiers ?? [])
      .filter(tier => tier.prices?.[currency] != null && Number(tier.minQuantity) <= Number(quantity))
      .sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0] ?? null
  )
}

/** A price less a discount in percent, rounded to 4 decimals as the server books it; no discount leaves it alone. */
export function discounted(price, discountPercent) {
  if (price == null) return null
  if (!Number(discountPercent)) return Number(price)
  // price × (100 − d) / 100, rounded once to 4 decimals, HALF_UP like the server's BigDecimal. In whole units (price
  // in 10⁻⁸, percent in 10⁻⁴) and BigInt, so a half is exactly a half (floating point would round 0,15 € at 1,5 % to
  // 0,1477, not 0,1478) and a price with more decimals than the result is not rounded before the discount.
  const product = units(price, 8) * (BigInt(1e6) - units(discountPercent, 4))
  const divisor = BigInt(1e10)
  let result = product / divisor
  const rest = product % divisor
  if ((rest < 0 ? -rest : rest) * BigInt(2) >= divisor) result += product < 0 ? BigInt(-1) : BigInt(1)
  return Number(result) / 1e4
}

const units = (value, decimals) => BigInt(Number(value).toFixed(decimals).replace(".", ""))

/**
 * One unit of a row at `quantity`, delivered: tier, discount, the freight of one delivery spread over the quantity
 * (none from the order value `freeFrom` on), then Skonto off goods and freight together.
 *
 * @param row `{ prices, tiers }` of a price row
 * @param conditions the row's supplier's `{ discountPercent, cashDiscount, freight }`, or null
 * @returns null without a price in `currency`, else `{ listPrice, unitPrice, freight, freightUnknown,
 *   cashDiscountPercent, effective, tier }` — `freight` is the delivery's, `effective` the delivered unit price
 */
export function effectivePrice(row, conditions, quantity, currency) {
  const listPrice = tierPrice(row.prices, row.tiers, quantity, currency)
  if (listPrice == null || !(Number(quantity) > 0)) return null
  const unitPrice = discounted(listPrice, conditions?.discountPercent)
  const goods = unitPrice * Number(quantity)

  const freightData = conditions?.freight
  const perDelivery = freightData?.perDelivery?.[currency]
  const freeFrom = freightData?.freeFrom?.[currency]
  // Freight kept in another currency only: not counted, and said so rather than shown as free.
  const freightUnknown =
    Boolean(freightData?.perDelivery && Object.keys(freightData.perDelivery).length) && perDelivery == null
  const freight = perDelivery == null || (freeFrom != null && goods >= Number(freeFrom)) ? 0 : Number(perDelivery)

  const cashDiscountPercent = Number(conditions?.cashDiscount?.percent) || 0
  const total = (goods + freight) * (1 - cashDiscountPercent / 100)
  return {
    listPrice: Number(listPrice),
    unitPrice,
    freight,
    freightUnknown,
    cashDiscountPercent,
    effective: total / Number(quantity),
    tier: reachedTier(row.tiers, quantity, currency),
  }
}
